import { ADD_STEP_PRESETS, type AddStepPresetId, type WorkflowStepType } from "@sagot/constants";
import { freshStepId, newStep, pathChoices, removeStep, addStep, stepIds } from "../../definition-model";
import type { Definition, Step } from "../../types";
import { firstExitOf, withFirstExit } from "./step-exits";

/** A step type, with the settings of a preset ("Close conversation") when one was picked. */
export interface AddStepChoice {
  type: WorkflowStepType;
  preset?: AddStepPresetId;
}

/** The step a preset stands for; any other step comes back unchanged. */
export function applyPreset(step: Step, preset: AddStepPresetId | undefined): Step {
  if (!preset) return step;
  const { status } = ADD_STEP_PRESETS[preset];
  return step.type === "set_status" ? { ...step, status } : step;
}

/** A new step of the chosen type with its defaults (`firstPath` is where a new Branch or Go to path leads at first). */
export function createStep(choice: AddStepChoice, id: string, firstPath: string): Step {
  return applyPreset(newStep(choice.type, id, firstPath), choice.preset);
}

export interface ReplacedStep {
  definition: Definition;
  /** Position of the new step in its path (the old step's position). */
  index: number;
  /** The step that was replaced. */
  replaced: Step;
}

/**
 * Swaps the last step of a path for a new step of another type. The new step gets a fresh id and its defaults; where both
 * steps have a first exit (a button, a branch, an outcome), the new step's first exit leads to the same path, so the part
 * of the workflow behind it stays connected. The old step's other exits and its translations go with it.
 */
export function replaceLastStep(definition: Definition, pathId: string, choice: AddStepChoice): ReplacedStep | null {
  const path = definition.paths[pathId];
  const replaced = path?.steps.at(-1);
  if (!path || !replaced) return null;
  const index = path.steps.length - 1;
  const without = removeStep(definition, pathId, index);
  const firstPath = pathChoices(without, pathId)[0] ?? pathId;
  const fresh = createStep(choice, freshStepId(choice.type, stepIds(without)), firstPath);
  const carried = firstExitOf(replaced);
  const step = carried ? withFirstExit(fresh, carried) : fresh;
  return { definition: addStep(without, pathId, step), index, replaced };
}
